import React, { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { untaggedListData } from 'service/service';
import CustomAnalysisTableBase from './CustomAnalysisTableBase';

const jobColumnMapping: Record<string, string> = {
    workspace_id: 'Workspace Id',
    workspace_name: 'Workspace Name',
    compute_type: 'Compute Type',
    job_name: 'Job Name',
    latest_cluster_change_date: 'Latest Cluster Change Date',
    untagged_cluster_count: 'Untagged Cluster Count',
    owned_by: 'Owned By',
};

const pipelineColumnMapping: Record<string, string> = {
    workspace_id: 'Workspace Id',
    workspace_name: 'Workspace Name',
    pipeline_type: 'Pipeline Type',
    pipeline_name: 'Pipeline Name',
    untagged_pipeline_count: 'Untagged Pipeline Count',
    latest_change_time: 'Latest Change Time',
    created_by: 'Created By'
};

const mlFlowColumnMapping: Record<string, string> = {
    workspace_id: 'Workspace Id',
    workspace_name: 'Workspace Name',
    experiment_id: 'Experiment Id',
    run_name: 'Run Name',
    untagged_run_count: 'Untagged Run Count',
    latest_update_time: 'Latest Update Time',
    created_by: 'Created By'
};

const warehouseColumnMapping: Record<string, string> = {
    workspace_id: 'Workspace Id',
    workspace_name: 'Workspace Name',
    warehouse_name: 'Warehouse Name',
    warehouse_type: 'Warehouse Type',
    warehouse_size: 'Warehouse Size',
    untagged_warehouse_count: 'Untagged Warehouse Count',
    latest_change_time: 'Latest Change Time',
    created_by: 'Created By'
};

const getColumnMapping = (type: string) => {
    if (type === 'Job' || type === 'Interactive') return jobColumnMapping;
    if (type === 'MATERIALIZED_VIEW' || type === 'ETL_PIPELINE' || type === "DATABASE_TABLE_SYNC") return pipelineColumnMapping;
    if (type === 'ML Flow') return mlFlowColumnMapping;
    if (type === 'PRO' || type === 'CLASSIC' || type === 'SERVERLESS') return warehouseColumnMapping;
    return jobColumnMapping;
};

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const UntaggedListTablePage = () => {
    const query = useQuery();
    const computeType: any = query.get('type') || 'Job';
    const mapping = useMemo(() => getColumnMapping(computeType), [computeType]);
    const defaultColumns = useMemo(() => Object.values(mapping), [mapping]);

    const filterTableDataFn = useCallback((data: any[], currentMapping: Record<string, string>) =>
        data.map((row: any) => {
            const filtered: Record<string, any> = {};
            Object.keys(currentMapping).forEach((key) => {
                if ((key === "latest_cluster_change_date") && row[key]) {
                    filtered[key] = new Date(row[key]).toLocaleDateString();
                } else if (key === "total_dbu" && typeof row[key] === "number") {
                    filtered[key] = row[key].toLocaleString();
                } else if (key === "total_compute_cost" && typeof row[key] === "number") {
                    filtered[key] = row[key].toLocaleString('en-US', { style: 'currency', currency: 'USD' });
                } else {
                    filtered[key] = row[key];
                }
            });
            return filtered;
        }), []);

    return (
        <CustomAnalysisTableBase
            fetchService={untaggedListData}
            columnMapping={mapping}
            defaultColumns={defaultColumns}
            filterTableDataFn={filterTableDataFn}
            breadcrumbLabel="Untagged Resources"
            tableTitle={() => computeType ? `Untagged List - ${computeType}` : 'Untagged List'}
            tableType="untagged-list"
            extraParams={{ type: computeType }}
            preserveFilterKey="untagged_filters"
            isAutoWidth={true}
        />
    );
};

export default UntaggedListTablePage;
